import {
  ActionIcon,
  Badge,
  Button,
  Card,
  CopyButton,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCircleCheck,
  IconCircleDashed,
  IconClockHour4,
  IconCopy,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  ARTWORK_STATUS_LABELS,
  ARTWORK_STATUSES,
  SAMPLE_PACK_LABELS,
  SAMPLE_STATUS_LABELS,
  SAMPLE_STATUSES,
  type ArtworkStatus,
  type SampleDto,
  type SampleEventDto,
  type SampleStatus,
} from "@mexp/shared";
import {
  formatBusinessDate,
  formatDate,
  formatDateTime,
  fromNow,
} from "@/lib/format";
import { useRequestSample } from "../api";

const SAMPLE_COLOR: Record<SampleStatus, string> = {
  requested: "gray",
  in_preparation: "brand",
  dispatched: "violet",
  delivered: "teal",
};
const ARTWORK_COLOR: Record<ArtworkStatus, string> = {
  pending: "gray",
  in_design: "brand",
  in_review: "violet",
  approved: "teal",
};

const FIRST = 4;

/** One track (the sample, or its artwork) as a short list: each stage, and when it was reached. */
function Stages<K extends string>({
  title,
  keys,
  labels,
  current,
  dates,
  color,
  pending,
}: {
  title: string;
  keys: readonly K[];
  labels: Record<K, string>;
  current: K;
  dates: Record<K, string | null>;
  color: string;
  /** A caption for the next stage, e.g. the expected delivery. */
  pending?: Partial<Record<K, string>>;
}) {
  const at = keys.indexOf(current);
  const finished = at === keys.length - 1;
  return (
    <Stack gap={6}>
      <Group justify="space-between" gap="xs">
        <Text
          fz="xs"
          fw={600}
          c="dimmed"
          tt="uppercase"
          style={{ letterSpacing: "0.04em" }}
        >
          {title}
        </Text>
        <Badge size="sm" variant="light" color={color}>
          {labels[current]}
        </Badge>
      </Group>
      <Stack
        gap={4}
        component="ol"
        style={{ listStyle: "none", margin: 0, padding: 0 }}
        aria-label={title}
      >
        {keys.map((k, i) => {
          const state =
            finished || i < at ? "done" : i === at ? "current" : "next";
          const Icon =
            state === "done"
              ? IconCircleCheck
              : state === "current"
                ? IconClockHour4
                : IconCircleDashed;
          const when =
            state === "next"
              ? (pending?.[k] ?? null)
              : dates[k]
                ? formatDate(dates[k]!)
                : null;
          return (
            <Group
              key={k}
              component="li"
              justify="space-between"
              wrap="nowrap"
              gap="sm"
              aria-current={state === "current" ? "step" : undefined}
            >
              <Group gap={8} wrap="nowrap">
                <Icon
                  size={16}
                  stroke={1.8}
                  color={
                    state === "next"
                      ? "var(--mantine-color-gray-5)"
                      : `var(--mantine-color-${color}-light-color)`
                  }
                  aria-hidden
                />
                <Text
                  fz="sm"
                  fw={state === "current" ? 600 : 400}
                  c={state === "next" ? "dimmed" : undefined}
                >
                  {labels[k]}
                </Text>
              </Group>
              {when && (
                <Text fz="xs" c="dimmed" className="tabular nowrap">
                  {when}
                </Text>
              )}
            </Group>
          );
        })}
      </Stack>
    </Stack>
  );
}

function Line({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" gap="md">
      <Text fz="sm" c="dimmed" className="nowrap">
        {label}
      </Text>
      <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
        {children}
      </Group>
    </Group>
  );
}

/** Courier, tracking number (copyable) and expected delivery, once BD has them. */
function Shipping({ s }: { s: SampleDto }) {
  if (!s.courierName && !s.trackingNo && !s.expectedDelivery) return null;
  return (
    <Stack gap={6}>
      {s.courierName && (
        <Line label="Courier">
          <Text fz="sm">{s.courierName}</Text>
        </Line>
      )}
      {s.trackingNo && (
        <Line label="Tracking">
          <Text
            fz="sm"
            ff="monospace"
            fw={600}
            style={{ overflowWrap: "anywhere" }}
          >
            {s.trackingNo}
          </Text>
          <CopyButton value={s.trackingNo} timeout={1600}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
                <ActionIcon
                  variant="subtle"
                  color={copied ? "teal" : "gray"}
                  size="sm"
                  onClick={copy}
                  aria-label="Copy the tracking number"
                >
                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Line>
      )}
      {s.expectedDelivery && (
        <Line label={s.status === "delivered" ? "Was expected" : "Expected"}>
          <Text fz="sm" className="tabular">
            {formatBusinessDate(s.expectedDelivery)}
          </Text>
        </Line>
      )}
    </Stack>
  );
}

/** What an update changed; the first one is the request itself (a sample only moves forward). */
const change = (h: SampleEventDto) =>
  h.status === "requested"
    ? "Sample requested"
    : [
        h.status ? `Sample: ${SAMPLE_STATUS_LABELS[h.status]}` : null,
        h.artworkStatus
          ? `Artwork: ${ARTWORK_STATUS_LABELS[h.artworkStatus]}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ") || "Details updated";

/** Who changed what, newest first. */
function Updates({ history }: { history: SampleEventDto[] }) {
  const [all, setAll] = useState(false);
  const items = [...history].reverse();
  const shown = all ? items : items.slice(0, FIRST);
  return (
    <Stack gap={8}>
      <Text
        fz="xs"
        fw={600}
        c="dimmed"
        tt="uppercase"
        style={{ letterSpacing: "0.04em" }}
      >
        Updates
      </Text>
      {shown.map((h, i) => (
        <Stack key={`${h.at}-${i}`} gap={2}>
          <Text fz="sm">{change(h)}</Text>
          {h.note && (
            <Text fz="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
              “{h.note}”
            </Text>
          )}
          <Tooltip label={formatDateTime(h.at)}>
            <Text fz="xs" c="dimmed" w="fit-content">
              {h.by} · {fromNow(h.at)}
            </Text>
          </Tooltip>
        </Stack>
      ))}
      {items.length > FIRST && (
        <Button
          variant="subtle"
          size="compact-sm"
          w="fit-content"
          onClick={() => setAll((v) => !v)}
        >
          {all ? "Show the latest only" : `Show all ${items.length} updates`}
        </Button>
      )}
    </Stack>
  );
}

/**
 * The sample asked for with the request, for RA and admins: its stages, the artwork for a commercial one, the courier
 * and who changed what. Read-only here: BD updates it in BDS.
 */
export function SampleTracking({
  requestId,
  cancelled,
}: {
  requestId: string;
  cancelled: boolean;
}) {
  const query = useRequestSample(requestId);
  const s = query.data;
  if (!s) return null;
  const waitingOnArtwork =
    s.packType === "commercial" &&
    s.status === "requested" &&
    s.artworkStatus !== "approved";
  return (
    <Card>
      <Stack gap="md">
        <Stack gap={2}>
          <Group justify="space-between" wrap="nowrap">
            <Text fw={600}>Sample</Text>
            <Badge variant="outline" color="gray" tt="none">
              {SAMPLE_PACK_LABELS[s.packType]}
            </Badge>
          </Group>
          <Text fz="xs" c="dimmed">
            {cancelled && s.status !== "delivered"
              ? "The request is cancelled: BD can no longer update the sample."
              : "BD updates the sample and artwork in BDS."}
          </Text>
        </Stack>
        {s.artworkStatus && s.artworkDates && (
          <Stages
            title="Artwork"
            keys={ARTWORK_STATUSES}
            labels={ARTWORK_STATUS_LABELS}
            current={s.artworkStatus}
            dates={s.artworkDates}
            color={ARTWORK_COLOR[s.artworkStatus]}
          />
        )}
        <Stages
          title="Sample"
          keys={SAMPLE_STATUSES}
          labels={SAMPLE_STATUS_LABELS}
          current={s.status}
          dates={s.stageDates}
          color={SAMPLE_COLOR[s.status]}
          pending={
            s.expectedDelivery
              ? {
                  delivered: `Expected ${formatBusinessDate(s.expectedDelivery)}`,
                }
              : undefined
          }
        />
        {waitingOnArtwork && (
          <Text fz="xs" c="dimmed">
            Preparation starts once the artwork is approved.
          </Text>
        )}
        <Shipping s={s} />
        <Updates history={s.history} />
      </Stack>
    </Card>
  );
}
